import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "@images/logo.svg";
import LoginPage from "@pages/LoginPage";
import { authApi } from "@services/contentApi";

import { ReactComponent as BellIcon } from "@icons/bell.svg";
import { ReactComponent as SearchIcon } from "@icons/search.svg";
import { Film, Home, Shapes, Tv, UserRound } from "lucide-react";

const NAV_ITEMS = [
  { title: "Главная", path: "/" },
  { title: "Фильмы", path: "/movies" },
  { title: "Сериалы", path: "/series" },
  { title: "Мультфильмы", path: "/cartoons" },
  { title: "Аниме", path: "/anime" },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState(() => NAV_ITEMS.find((item) => item.path === location.pathname)?.title || null);
  const [underlineWidth, setUnderlineWidth] = useState(0);
  const [underlineLeft, setUnderlineLeft] = useState(0);
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isOverHero, setIsOverHero] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const refs = useRef({});
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollTimerRef = useRef(null);

  useEffect(() => {
    setActiveNav(NAV_ITEMS.find((item) => item.path === location.pathname)?.title || null);
  }, [location.pathname]);

  useEffect(() => {
    authApi.me().then(({ user }) => setCurrentUser(user)).catch(() => setCurrentUser(null));
  }, [location.pathname]);

  useEffect(() => {
    const activeEl = refs.current[activeNav];
    if (activeEl) {
      const rect = activeEl.getBoundingClientRect();
      const navRect = activeEl.parentElement.getBoundingClientRect();
      setUnderlineWidth(rect.width + 15);
      setUnderlineLeft(rect.left - navRect.left + rect.width / 2);
    }
  }, [activeNav]);

  useEffect(() => {
    const updateHeaderSurface = () => {
      setIsOverHero(window.scrollY < window.innerHeight - 99);
    };

    const handleScroll = () => {
      updateHeaderSurface();
      setIsHeaderVisible(false);

      window.clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(() => {
        updateHeaderSurface();
        setIsHeaderVisible(true);
      }, 180);
    };

    updateHeaderSurface();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateHeaderSurface);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateHeaderSurface);
      window.clearTimeout(scrollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!searchActive) return;

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 180);

    return () => window.clearTimeout(focusTimer);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;

      setSearchValue("");
      setSearchActive(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [searchActive]);

  return (
    <header className="site-header"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 76,
        display: "flex",
        alignItems: "center",
        padding: "0 60px",
        opacity: isHeaderVisible ? 1 : 0,
        transform: isHeaderVisible ? "translateY(0)" : "translateY(-100%)",
        pointerEvents: isHeaderVisible ? "auto" : "none",
        transition:
          "opacity 0.35s ease, transform 0.35s ease, background 0.35s ease",
        backdropFilter: "blur(0px)",
        WebkitBackdropFilter: "blur(0px)",
        background: isOverHero ? "rgba(0,0,0,0)" : "#000000",
      }}
    >
      <div className="site-header-logo" style={{ minWidth: 144 }}>
        <img src={Logo} alt="logo" />
      </div>

      <nav className="site-desktop-nav"
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 30,
        }}
      >
        {NAV_ITEMS.map((item) => {
          const active = activeNav === item.title;
          return (
            <button
              key={item.path}
              ref={(el) => (refs.current[item.title] = el)}
              onClick={() => navigate(item.path)}
              style={{
                position: "relative",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: active ? "#fff" : "rgba(255,255,255,0.6)",
                paddingBottom: 1,
                transition: "color 0.25s",
              }}
            >
              {item.title}
            </button>
          );
        })}

        <span
          style={{
            position: "absolute",
            top: 31,
            left: underlineLeft,
            transform: "translateX(-50%)",
            width: underlineWidth,
            height: 2,
            borderRadius: 10,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0) 0%, #3f5cff 25%, #ffffff 50%, #3f5cff 75%, rgba(0,0,0,0) 100%)",
            filter: "blur(0.3px)",
            transition: "left 0.25s, width 0.25s",
            opacity: activeNav ? 1 : 0,
          }}
        />
      </nav>

      <div className="site-header-actions"
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div className="site-header-search"
          ref={searchWrapRef}
          onClick={() => {
            setSearchActive(true);
            window.setTimeout(() => searchInputRef.current?.focus(), 0);
          }}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: searchActive ? 280 : 46,
            height: 46,
            borderRadius: 12,
            overflow: "hidden",
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(10px)",
            cursor: "text",
            transition:
              "width 0.35s cubic-bezier(.22, 1, .36, 1), background 0.25s ease",
          }}
        >
          <button
            onClick={() => setSearchActive(true)}
            aria-label="Search"
            style={{
              position: "absolute",
              left: searchActive ? 12 : 13,
              top: "50%",
              transform: "translateY(-50%)",
              width: 20,
              height: 20,
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              transition: "left 0.35s cubic-bezier(.22, 1, .36, 1)",
            }}
          >
            <SearchIcon width={20} height={20} />
          </button>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Поиск..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setSearchActive(true)}
            onBlur={() => {
              if (!searchValue.trim()) setSearchActive(false);
            }}
            style={{
              width: "100%",
              height: "100%",
              opacity: searchActive ? 1 : 0,
              transition: "opacity 0.22s ease 0.08s",
              padding: "0 46px 0 46px",
              borderRadius: 12,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 14,
              pointerEvents: searchActive ? "auto" : "none",
            }}
          />
          <button
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              setSearchValue("");
              searchInputRef.current?.focus();
            }}
            aria-label="Clear search"
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "none",
              background: "rgba(255,255,255,0.18)",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 0,
              opacity: searchActive && searchValue ? 1 : 0,
              pointerEvents: searchActive && searchValue ? "auto" : "none",
              transition: "opacity 0.2s ease, background 0.2s ease",
            }}
          >
            <svg
              width="9"
              height="9"
              viewBox="0 0 9 9"
              aria-hidden="true"
              style={{ display: "block" }}
            >
              <path
                d="M1 1L8 8M8 1L1 8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            ×
          </button>
        </div>

        <button className="site-subscribe-button jost-m"
          style={{
            background: "#354BCA",
            border: "none",
            color: "#fff",
            padding: "12px 14px",
            borderRadius: 12,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Оформить подписку
        </button>

        <button className="site-bell-button"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 10,
            display: "flex",
          }}
        >
          <BellIcon width={22} height={22} />
        </button>

        <button className="site-user-button"
          onClick={() => currentUser ? navigate("/profile") : setLoginOpen(true)}
          aria-haspopup={currentUser ? undefined : "dialog"}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.7)",
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          {currentUser ? currentUser.firstName || currentUser.login : "Войти"}
        </button>
      </div>
      <nav className="mobile-app-nav" aria-label="Мобильная навигация">
        {[{ title: "Главная", path: "/", icon: Home }, { title: "Фильмы", path: "/movies", icon: Film }, { title: "Сериалы", path: "/series", icon: Tv }, { title: "Жанры", path: "/genres", icon: Shapes }].map(({ title, path, icon: Icon }) => <button key={path} className={location.pathname === path ? "active" : ""} onClick={() => navigate(path)}><Icon size={20}/><span>{title}</span></button>)}
        <button className={location.pathname === "/profile" ? "active" : ""} onClick={() => currentUser ? navigate("/profile") : setLoginOpen(true)}><UserRound size={20}/><span>{currentUser ? "Профиль" : "Войти"}</span></button>
      </nav>
      {loginOpen && createPortal(
        <LoginPage modal onClose={() => setLoginOpen(false)} />,
        document.body
      )}
    </header>
  );
}
